import { tap } from 'rxjs';
import { inject } from '@angular/core';
import { StateFieldType, isReportTabRulesKey } from '@navstation-portal/reporting/data-model/vessel-report';
import { CarryOverWrite } from '@navstation-portal/reporting/util/form-rules';
import { signalStoreFeature, type } from '@ngrx/signals';
import { Dispatcher, Events, withEventHandlers } from '@ngrx/signals/events';
import { FORM_STORE_EVENTS } from './form-store-events.map';
import { reportEvents } from './report-events';

/**
 * What this feature needs from the store it composes into. The typed input
 * states it, so the compiler checks that the store above holds it. An input
 * erases no member: `signalStore` gives the store every feature's output.
 *
 * A type alias and not an interface, because the input must fit
 * `MethodsDictionary`, and only an alias gets an implicit index signature.
 */
type CarryOverStoreMethods = {
  carryOverWritesOnCreate: () => CarryOverWrite[];
};

/**
 * Puts every value the template carries into a new report.
 *
 * This is the `onCreate` half of carry-over. `createNewReport` dispatches
 * `carryOverOnCreate` once, after the tab stores build their form state and
 * after the hardcoded shore-power patch, so the template wins over both.
 * Nothing else dispatches that event, so opening a report that already exists
 * changes no field.
 *
 * **Compose this after `withComputed` and `withMethods`.** It calls
 * `carryOverWritesOnCreate`, which reads `ruleContext`, and a feature sees only
 * the members the features above it added. `withReportEventHandlers` sits
 * before those and cannot see either, which is the reason the trigger is an
 * event rather than a direct call.
 *
 * It reads the writes once, so every entry sees one context and no entry reads
 * a value another entry wrote. It writes through the same events a form control
 * writes through, so a carried value and a typed value reach the state the one
 * way. `updateFormStateValue` sets the value alone, which leaves the field
 * pristine and untouched, as the store's init leaves it.
 *
 * **The template and the tab stores both carry today.** Where a field has an
 * `onCreate` entry, `initWithCarryOver` writes it first and this writes over
 * it. Moving a field to the template means emptying its `initStateField` call,
 * never deleting it: the key must stay, or this write has no field to land on.
 * See the form-rule language doc.
 */
// The unused type parameter makes TypeScript check this input after it infers
// the features above, as withOffshoreReportComputed does. Without it, the
// signalStore call fails to infer and the store loses its member types.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function withCarryOverOnCreate<_>() {
  return signalStoreFeature(
    type<{ methods: CarryOverStoreMethods }>(),
    withEventHandlers((store) => {
      const events = inject(Events);
      const dispatcher = inject(Dispatcher);

      return {
        carryOverOnCreate$: events.on(reportEvents.carryOverOnCreate).pipe(
          tap(() => {
            for (const { tabId, sectionId, fieldId, value, index, property } of store.carryOverWritesOnCreate()) {
              // A rules key the template holds and the form does not takes no
              // write.
              if (!isReportTabRulesKey(tabId)) continue;
              dispatcher.dispatch(
                FORM_STORE_EVENTS[tabId].updateFormStateValue({
                  sectionId,
                  fieldId,
                  value: value as StateFieldType,
                  index,
                  property,
                })
              );
            }
          })
        ),
      };
    })
  );
}
